import { useMemo } from 'react';
import type { Command, FilterCondition, Perspective } from '@integrate/contracts';
import {
  HEADCOUNT,
  closeMonthOf,
  computeStageBridge,
  computeStrandedTimeline,
  countsInHeadcount,
  fmt,
  fmtMoney,
  fmtMoneyCompact,
  loadedCost,
  passesFilters,
  strandedEligible,
  strandedPlanOf,
  type SeparationState,
  type StrandedCarrier,
} from '@integrate/core';
import { LiveField } from './live-input.js';
import { StatTile } from '../ui/StatTile.js';
import { Section } from './sections.js';

/**
 * The RemainCo phase panel, ported from `p5PanelHtml` / `wireP5Panel` (orgchart.html:6457–6498).
 *
 * The last phase, and the one nobody asks for until it is too late. Everything up to here has been about
 * the organisation that leaves. This is about the one that stays, and the bill it is left holding.
 *
 * Stranded cost is the arithmetic of a half-emptied job. Somebody who spent 40% of their time serving the
 * divested business does not become 40% cheaper on Day 1 — they are still employed, still paid in full,
 * and 40% of their loaded cost is now buying nothing. Multiply that by everybody in the same position and
 * it is the number that turns a good deal into a bad one.
 *
 * Two things reduce it, and the panel names both. TSA fee income offsets it for as long as the agreements
 * run, which is temporary by construction — the tile says when it stops. And the mitigation plan is the
 * permanent half: rightsize somebody out (severance now, their full loaded cost saved after) or redeploy
 * them into work that still exists (no cash effect, capacity absorbed). What is left when the income
 * stops and the plan is done is the residual, and that is the headline.
 */
export function RemainCoPanel({
  state,
  perspective,
  filters,
  readOnly,
  scenarioId,
  onDispatch,
  onOpenAssumptions,
  onOpenAnalytics,
}: {
  state: SeparationState;
  /** Passed rather than assumed: the carrier list honours the chart's filters, which resolve through it. */
  perspective: Perspective;
  /** This tab's active filters, for the same reason. */
  filters: readonly FilterCondition[];
  readOnly: boolean;
  scenarioId: string;
  onDispatch(command: Command | readonly Command[]): Promise<boolean>;
  onOpenAssumptions(): void;
  /** Opens the analytics modal ON the stranded tab — a deep link, not a default. */
  onOpenAnalytics(): void;
}): JSX.Element | null {
  const anyDedication = state.employees.some((e) => e.dedication != null);

  const timeline = useMemo(
    () => (anyDedication ? computeStrandedTimeline(state) : null),
    [state, anyDedication],
  );
  const bridge = useMemo(() => computeStageBridge(state, { fn: HEADCOUNT }), [state]);

  /*
    The carriers, largest stranded cost first, following the chart's filters.

    `computeStrandedTimeline` already computes the same population for the economics; this list is sorted
    for reading and filtered for the view, which the timeline deliberately is not — the money is the money
    whatever the chart happens to be showing.
  */
  const carriers = useMemo(
    () => sortedCarriers(state, perspective, filters),
    [state, perspective, filters],
  );
  const unfiltered = useMemo(() => sortedCarriers(state, perspective, []), [state, perspective]);
  const hiddenByFilters = filters.length ? unfiltered.length - carriers.length : 0;

  // Eligible, carrying a plan, and outside filled headcount — so priced by nothing above.
  const strayPlans = useMemo(
    () =>
      state.employees
        .filter(
          (p) =>
            !countsInHeadcount(p) &&
            strandedEligible(state.design, p) &&
            strandedPlanOf(state.design, p) !== null,
        )
        .map((person) => ({
          person,
          d: person.dedication ?? 0,
          stranded: 0,
          plan: strandedPlanOf(state.design, person),
          closeMonth: closeMonthOf(person, state.assumptions),
        })),
    [state],
  );

  if (!state.employees.length) return null;

  if (!anyDedication || !timeline) {
    return (
      <Section
        testId="stranded-nodata"
        title="Stranded cost"
      >
        <p className="muted">
          Stranded-cost planning needs dedication data (0–100% of each person&rsquo;s work serving the
          carve-out). Map a Dedication column in phase 1 or enter values per person.
        </p>
      </Section>
    );
  }

  const mitigated = timeline.grossRunRate - timeline.residualRunRate;

  const setPlan = (carrier: StrandedCarrier, action: 'rightsize' | 'redeploy' | 'keep'): void => {
    /*
      NO MONTH, which is not the same as month 0.

      This used to send `carrier.plan ? carrier.plan.month : 6` — the frozen app's `prev ? prev.month : 6`
      (`:6441`), six months being its assumption about how long it takes to act on a stranded role —
      because the command schema defaulted the field to 0, "effective immediately", and only this panel
      knew better. That made the panel the sole holder of a rule the whole platform was supposed to
      follow, so a proposal or the assistant got the other answer. The schema now says what absent means
      and says it in the same words, so sending nothing is sending the rule rather than dodging it.
    */
    void onDispatch({
      type: 'setStrandedPlan',
      scenarioId,
      assignments: [{ employeeId: carrier.person.id, action: action === 'keep' ? null : action }],
    });
  };

  /*
    Returns the clamped value so the caller can put it back in the field.

    The field is uncontrolled and only remounts when the stored month changes, so a rejected or clamped
    entry — 99 against a ceiling of 36, or `abc` against 0 — would otherwise sit on screen against a
    model that holds something else. A plan read aloud from that screen is wrong. The shipped app writes
    the clamped value back for the same reason (`:6451`, `:5198`).
  */
  const setMonth = (carrier: StrandedCarrier, raw: string): number | null => {
    if (!carrier.plan) return null;
    // Floored at 1: the timeline collapses month 0 into month 1, so storing 0 recorded a month the
    // model does not distinguish and the history then quoted back as "M0".
    const month = Math.max(1, Math.min(36, Math.round(Number(raw) || 0)));
    if (month === carrier.plan.month) return month;
    void onDispatch({
      type: 'setStrandedPlan',
      scenarioId,
      assignments: [{ employeeId: carrier.person.id, action: carrier.plan.action, month }],
    });
    return month;
  };

  return (
    <>
      <Section
        testId="stranded-strip"
        title={
          <>
            RemainCo stranded cost{' '}
            <span className="pill" data-testid="residual-run-rate">
              {fmtMoneyCompact(timeline.residualRunRate)}
            </span>
          </>
        }
      >
        <p className="muted">
          People staying behind spend part of their time on the divested business — that share of their
          loaded cost is stranded after Day 1, until mitigated. Loaded cost is salary ×{' '}
          <button type="button" className="linkish" data-testid="asm-open-remainco" onClick={onOpenAssumptions}>
            {Number(state.assumptions.loadedCostMultiplier) || 1}
          </button>
          ; severance below uses the months rules from the same place.
        </p>
        <div className="pp-stats">
          <StatTile
            label="Gross stranded"
            value={fmtMoneyCompact(timeline.grossRunRate)}
            sub={`p.a. · ${fmt(timeline.carriers.length)} carriers`}
            testId="gross-stranded"
          />
          <StatTile
            label="TSA income"
            value={fmtMoneyCompact(timeline.tsaIncomeMonthly * 12)}
            sub={
              timeline.tsaIncomeMonthly
                ? `p.a. now · steps down as agreements end, last at M${timeline.tsaIncomeMonths}`
                : 'no received TSAs'
            }
          />
          <StatTile
            label="− Mitigated"
            value={fmtMoneyCompact(mitigated)}
            sub={`✕ ${fmt(timeline.rightsizedN)} rightsized · ↻ ${fmt(timeline.redeployedN)} redeployed`}
          />
          <StatTile
            label="= Residual"
            value={fmtMoneyCompact(timeline.residualRunRate)}
            sub={`steady state p.a. · ${fmt(timeline.residualN)} unmitigated`}
          />
        </div>

        {timeline.rightsizedN > 0 && (
          <>
            <div className="sp-row">
              <span className="k">One-off severance</span>
              <span className="v">{fmtMoney(timeline.severanceOneOff)}</span>
            </div>
            {/* The original's threshold, not a rounding of zero. */}
            {timeline.overRemovalRunRate > 0.004 && (
              <div
                className="sp-row"
                title="Rightsized carriers were only partly dedicated — the rest of their capacity still served RemainCo and leaves with them (backfill risk)"
              >
                <span className="k">⚠ Over-removal</span>
                <span className="v" data-testid="over-removal">
                  {fmtMoney(timeline.overRemovalRunRate)} p.a.
                </span>
              </div>
            )}
          </>
        )}

        <div className="sp-row">
          <span className="k">RemainCo after Day 1</span>
          <span className="v">{fmt(bridge.remainOrg)} people</span>
        </div>
        <div className="sp-row">
          <span className="k">− Planned rightsizing</span>
          <span className="v">{fmt(bridge.remainRightsize)}</span>
        </div>
        <div className="sp-row">
          <span className="k">= Steady state</span>
          <span className="v" data-testid="steady-state">
            {fmt(bridge.remainOrg - bridge.remainRightsize)} people
          </span>
        </div>
        {/*
          The copy this replaces named the wrong series, and did it the way the brief did: it promised
          "gross exposure stepping up as each agreement ends". Gross steps DOWN as the plan lands; INCOME
          steps down as agreements end; it is the NET that steps up. Three series, named.
        */}
        <button
          style={{ width: '100%', marginTop: 8 }}
          data-testid="open-stranded-analytics"
          onClick={onOpenAnalytics}
        >
          Open the stranded-cost timeline…
        </button>
        <p className="muted">
          Month by month: gross exposure stepping <strong>down</strong> as each mitigation lands, TSA fee
          income stepping down as each received agreement ends, and the <strong>net</strong> between them
          stepping <strong>up</strong> when the cover runs out. Unfiltered, unlike the carrier list below.
        </p>
      </Section>

      <Section
        testId="mitigation-plan"
        title={
          <>
            Mitigation plan{' '}
            <span className="pill">
              {fmt(timeline.rightsizedN + timeline.redeployedN)}/{fmt(timeline.carriers.length)}
            </span>
          </>
        }
      >
        <p className="muted">
          Carriers of stranded capacity, largest first. <strong>Rightsize</strong> = leaves RemainCo at
          month M (one-off severance, full loaded saving). <strong>Redeploy</strong> = absorbed into the
          remaining business (no cash effect). Also editable on each person&rsquo;s panel.
        </p>

        {/*
          People with a plan whom the figures above do not count.

          `strandedEligible` has no headcount test, so an open position or an inactive person can carry a
          plan — imported files do — while every consumer of it adds `countsInHeadcount` and prices them
          at nothing. Leaving them out of this list would badge the plan on their own panel and give no
          route back to it. Listed, and the gap named, as everywhere else on this platform.
        */}
        {strayPlans.length > 0 && (
          <p className="muted" data-testid="stranded-count-gap">
            {fmt(strayPlans.length)} of these are open positions or inactive people — listed so the plan
            can be changed, but not counted in the figures above.
          </p>
        )}

        <div className="picker-list" data-testid="carrier-list">
          {carriers.length === 0 && strayPlans.length === 0 ? (
            <p className="muted">
              No stranded-capacity carriers (Remain-side people with dedication &gt; 0).
            </p>
          ) : (
            carriers.slice(0, LIST_CAP).map((c) => (
              <div className="sp-row" key={c.person.id}>
                <span
                  className="k grow"
                  title={`${c.person.name} — ${Math.round(c.d * 100)}% dedicated to the carve-out`}
                >
                  {c.person.name}{' '}
                  <span className="muted num">
                    · {Math.round(c.d * 100)}% · {fmtMoneyCompact(c.stranded)}
                  </span>
                </span>
                <span className="v carrier-controls">
                  <select
                    data-testid={`carrier-action-${c.person.id}`}
                    value={c.plan ? c.plan.action : 'keep'}
                    disabled={readOnly}
                    onChange={(e) =>
                      setPlan(c, e.target.value as 'rightsize' | 'redeploy' | 'keep')
                    }
                  >
                    <option value="keep">Keep</option>
                    <option value="redeploy">Redeploy</option>
                    <option value="rightsize">Rightsize</option>
                  </select>
                  <LiveField
                    type="number"
                    data-testid={`carrier-month-${c.person.id}`}
                    /* M1 is the floor — the model cannot tell M0 from M1. See the person panel. */
                    min={1}
                    max={36}
                    step={1}
                    // Uncontrolled, and resynced rather than re-keyed: a key change is a remount, so the
                    // month a colleague set used to destroy the box somebody was typing a month into.
                    value={c.plan ? String(c.plan.month) : ''}
                    placeholder="M"
                    title="Effective month after Day 1"
                    disabled={readOnly || !c.plan}
                    onCommit={(text, el) => {
                      const applied = setMonth(c, text);
                      if (applied !== null) el.value = String(applied);
                    }}
                  />
                </span>
              </div>
            ))
          )}
          {strayPlans.map((c) => (
            <div className="sp-row" key={c.person.id} data-testid={`stray-plan-${c.person.id}`}>
              <span className="k grow" title={`${c.person.name} — not counted in the figures above`}>
                {c.person.name} <span className="muted num">· not counted</span>
              </span>
              <span className="v carrier-controls">
                <select
                  data-testid={`carrier-action-${c.person.id}`}
                  value={c.plan ? c.plan.action : 'keep'}
                  disabled={readOnly}
                  onChange={(e) => setPlan(c, e.target.value as 'rightsize' | 'redeploy' | 'keep')}
                >
                  <option value="keep">Keep</option>
                  <option value="redeploy">Redeploy</option>
                  <option value="rightsize">Rightsize</option>
                </select>
              </span>
            </div>
          ))}
        </div>
        {carriers.length > LIST_CAP && (
          <p className="muted" data-testid="carrier-capped">
            Showing {LIST_CAP} of {fmt(carriers.length)} (largest stranded cost first).
          </p>
        )}
        {hiddenByFilters > 0 && (
          <p className="muted" data-testid="carrier-filtered">
            {fmt(hiddenByFilters)} carrier{hiddenByFilters === 1 ? '' : 's'} hidden by chart filters.
          </p>
        )}
      </Section>
    </>
  );
}

const LIST_CAP = 60;

/**
 * The carriers, ported from `strandedCarriers` (`:6409`).
 *
 * Sorted by stranded cost descending — the money, not the headcount, because a 20%-dedicated executive
 * strands more than a fully-dedicated junior. Name breaks ties so the order is stable between renders.
 *
 * Built here rather than taken from `computeStrandedTimeline().carriers` because the two answer different
 * questions: the timeline prices everybody regardless of what the chart is showing, while this list is a
 * work queue and follows the view. Keeping them separate is why the tile and the list can legitimately
 * disagree, and why the hidden-by-filters note exists to say so.
 */
function sortedCarriers(
  state: SeparationState,
  perspective: Perspective,
  filters: readonly FilterCondition[],
): StrandedCarrier[] {
  const filterCtx = { targetOverrides: state.targetOverrides, design: state.design };
  return state.employees
    .filter((p) => countsInHeadcount(p) && strandedEligible(state.design, p))
    .filter((p) => passesFilters(filterCtx, p, perspective, filters))
    .map((person) => ({
      person,
      d: person.dedication ?? 0,
      stranded: (person.dedication ?? 0) * loadedCost(person, state.assumptions),
      plan: strandedPlanOf(state.design, person),
      closeMonth: closeMonthOf(person, state.assumptions),
    }))
    .sort((a, b) => b.stranded - a.stranded || a.person.name.localeCompare(b.person.name));
}
